import React from 'react';
import { useNavigate } from 'react-router';
import { isTeamInvitation } from '../../../../../../../domain/accounts';
import { RegistrationContainerConfig } from '../../../../../base';
import { RegistrationAcceptContextProvider } from '../../context';
import { buildServicePayload } from './acceptOrganizationFactory';
import { RegistrationAcceptOrganizationController } from './acceptOrganizationInterface';
import {
    AcceptOrganizationContainerProps,
    AcceptOrganizationViewProps,
} from './acceptOrganizationProps';

export function createRegistrationAcceptOrganizationContainer(
    config: RegistrationContainerConfig & {
        accept: RegistrationAcceptContextProvider;
    },
    controller: RegistrationAcceptOrganizationController
): React.FC<AcceptOrganizationContainerProps> {
    const {
        infra: { toaster },
        service: { invitation: invitationService },
        accept: { useContext },
    } = config;
    return ({ as: View, ...containerProps }) => {
        const toast = toaster.useToast();
        const mutation = invitationService.useAccept();
        const context = useContext();

        // NOTE when invitee is present the invitation is for an existing organization
        // so we should not create a new one
        const isReadonly = isTeamInvitation(context.data.invitation);

        const props = controller.useProps({
            stepper: context.stepper,
            form: {
                action: {
                    label: 'Continue',
                },
                id: 'invite_accept_organization_step',
                form: context.form.organization,
                readonly: isReadonly,
                async onSubmit(values) {
                    const payload = buildServicePayload(context, {
                        step: context.stepper.selected,
                        token: context.token,
                        invitation: context.invitation,
                        profile: context.form.profile.getValues(),
                        organization: values,
                    });
                    const response = await mutation.mutateAsync(payload);
                    context.stepper.onChange(context.stepper.selected + 1);
                },
                onSubmitError(error) {
                    toast({
                        kind: 'error',
                        description: `Form submission failed`,
                        durationMs: 5000,
                    });
                },
                metadata: context.metadata,
            },
            layout: {
                stepper: context.stepper,
                navigate: useNavigate(),
            },
            page: {
                metadata: context.metadata,
            },
        });
        return <View {...props} />;
    };
}
